import { TextButton } from '@twistopayments/design-system/components/atoms/button'
import type { Variants } from '@twistopayments/design-system/components/atoms/button/text-button/text-button.styles'
import { Modal } from '@twistopayments/design-system/components/atoms/modal'
import { Typography } from '@twistopayments/design-system/components/atoms/typography'
import type { IconComponent } from '@twistopayments/design-system/icons'
import {
  CloseIcon,
  ExpensesOutlineIcon,
  InstallmentsOutlineIcon,
  PhysicalCardsIcon,
  PigIcon,
  TwistoAppLogoShortIcon,
} from '@twistopayments/design-system/icons'
import { useRouter } from 'next/router'

import type {
  NavigationModalBenefitItemComponent,
  NavigationSignUpButtonComponent,
} from '@twisto/api-cms'
import { useOpen } from '@twisto/hooks'

import { useAdditionalData } from 'homepage/libs/context'
import { RewriteSlugs, appendSearchParams } from 'homepage/utils'

import * as styles from './authentication.styles'

type BenefitsIcon = NavigationModalBenefitItemComponent['icon']
type Props = {
  source?: 'main' | 'modal'
  variant?: Variants
}

const getDividedHeadline = (headline: string) => (
  <>
    {headline.split('<br>').map((line, index) => (
      <Typography key={index} css={styles.headline}>
        {line}
        <br />
      </Typography>
    ))}
  </>
)

const getIcon = (icon: BenefitsIcon) => {
  switch (icon) {
    case 'ExpensesOutlineIcon':
      return ExpensesOutlineIcon
    case 'InstallmentsOutlineIcon':
      return InstallmentsOutlineIcon
    case 'PigIcon':
      return PigIcon
    case 'PhysicalCardsIcon':
      return PhysicalCardsIcon
    default:
      return null
  }
}

export const SignUpButton = ({
  text,
  variant,
  ctaUrl,
  modalCtaText,
  modalBenefitItems,
  modalBenefitsTitle,
  headline,
  enableModal,
}: NavigationSignUpButtonComponent & Props) => {
  const { signUpModalOpened } = useAdditionalData()
  const router = useRouter()

  const currentSearch = router.asPath.split('?')[1] ?? ''
  const ctaUrlWithParams = appendSearchParams(ctaUrl, currentSearch)

  const {
    isOpen,
    handleOpen,
    handleClose: closeModal,
  } = useOpen(signUpModalOpened)

  const handleButtonClick = () => {
    if (!enableModal) {
      return
    }

    handleOpen()
  }

  const handleClose = () => {
    if (signUpModalOpened) {
      router.push(
        router.asPath.replace(`${RewriteSlugs.signUpModal.source}/`, '')
      )
    }

    closeModal()
  }

  return (
    <>
      <TextButton
        css={styles.authButton}
        size="medium"
        to={enableModal ? undefined : ctaUrl}
        variant={variant}
        onClick={handleButtonClick}
      >
        {text}
      </TextButton>

      {enableModal && (
        <Modal
          noPadding
          withoutAnimation
          aria-label="video-stories"
          modalWrapperStyles={styles.benefitsModal}
          opened={isOpen}
          onClickOutside={handleClose}
          onClose={handleClose}
        >
          <div css={styles.modalContent}>
            <button css={styles.closeButton} onClick={handleClose}>
              <CloseIcon fontWeight="bold" height={15} width={15} />
            </button>
            <div css={styles.authPreview}>
              <TwistoAppLogoShortIcon height={50} width={50} />
              {headline && (
                <Typography align="center">
                  {getDividedHeadline(headline)}
                </Typography>
              )}
              <TextButton
                fullWidth
                css={styles.signUpPreviewButton}
                to={ctaUrlWithParams}
              >
                {modalCtaText}
              </TextButton>
            </div>
            <div css={styles.benefits}>
              <Typography css={styles.benefitsTitle}>
                {modalBenefitsTitle}
              </Typography>
              {modalBenefitItems && (
                <ul css={styles.benefitList}>
                  {modalBenefitItems.map(({ icon, title }, index) => {
                    const Icon = getIcon(icon) as IconComponent

                    return (
                      <li key={index}>
                        <Icon css={styles.icon} height={30} width={30} />
                        <Typography>{title}</Typography>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          </div>
        </Modal>
      )}
    </>
  )
}
